import { AbstractMesh } from '@babylonjs/core';
import { Engine } from '@babylonjs/core/Engines/engine';
import { DefaultLoadingScreen } from '@babylonjs/core/Loading/loadingScreen';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { DefaultRenderingPipeline } from '@babylonjs/core/PostProcesses/RenderPipeline/Pipelines/defaultRenderingPipeline';
import { Scene as BabylonScene } from '@babylonjs/core/scene';
import { GLTFFileLoader } from '@babylonjs/loaders/glTF/glTFFileLoader';
import { GLTF2Export, GLTFData } from '@babylonjs/serializers/glTF';

import { Article } from '../../interfaces/Article';
import { Base64Image } from '../../interfaces/Base64Image';
import { Configuration } from '../../interfaces/Configuration';
import { ConfigurationRight } from '../../interfaces/ConfigurationRight';
import { Dimensions } from '../../interfaces/Dimensions';
import { LogisticsStatus } from '../../interfaces/LogisticsStatus';
import { ReplacementChild } from '../../interfaces/ReplacementChild';
import { Slot } from '../../interfaces/Slot';
import { SlotDefinition } from '../../interfaces/SlotDefinition';
import ArticleController, { SlotLineUtil } from '../controllers/articleController';
import AssetsController from '../controllers/assetsController';
import CameraController, { CameraAction } from '../controllers/cameraController';
import EventsController, { EventsRegistry } from '../controllers/eventsController';
import LightController from '../controllers/lightController';
import ArticleNode from '../nodes/articleNode';
import Settings from '../settings/settings';
import MeshUtils from '../utils/meshUtils';

export default class Scene {
  private scene: BabylonScene;
  private cameraController: CameraController;
  private lightController: LightController;
  private eventsController: EventsController;
  private assetsController: AssetsController;
  private articleController: ArticleController;

  public mount(canvas: HTMLCanvasElement) {
    const engine = new Engine(canvas, true, { stencil: true }, true);
    this.scene = new BabylonScene(engine);

    this.assetsController = new AssetsController(this.scene);
    this.lightController = new LightController(this.scene);
    this.articleController = new ArticleController(this.scene);
    this.eventsController = new EventsController(this.scene);
    this.cameraController = new CameraController(this.scene);

    this.setupRendering();
    this.setupGLTFFileLoader();
    this.setupLoadingScreen();
    this.registerIdleAnimationListener();

    MeshUtils.createGround(this.scene);

    return this.scene;
  }

  private setupRendering() {
    const pipeline = new DefaultRenderingPipeline('default', true, this.scene, this.scene.cameras);
    pipeline.imageProcessingEnabled = false;
    const { OVERLAY_RENDERING_GROUP } = Settings;
    this.scene.setRenderingAutoClearDepthStencil(OVERLAY_RENDERING_GROUP, true, true, false);
  }

  private setupGLTFFileLoader() {
    GLTFFileLoader.HomogeneousCoordinates = true;
    GLTFFileLoader.IncrementalLoading = false;
  }

  private setupLoadingScreen() {
    const loadingScreen = this.scene.getEngine().loadingScreen;
    loadingScreen.loadingUIBackgroundColor = Settings.LOADING_SCREEN_COLOR;
    DefaultLoadingScreen.DefaultLogoUrl = Settings.BLANK_LOGO;
    DefaultLoadingScreen.DefaultSpinnerUrl = Settings.BLANK_LOGO;
  }

  private registerIdleAnimationListener() {
    document.removeEventListener('mousedown', this.handleMouseDownEvent);
    document.addEventListener('mousedown', this.handleMouseDownEvent);
  }

  private handleMouseDownEvent = () => {
    const selectedArticle = this.eventsController.selectedArticle();
    this.cameraController.disableIdleAnimation();
    !selectedArticle && this.cameraController.enableIdleAnimation();
  };

  public get events(): EventsRegistry {
    return this.eventsController as EventsRegistry;
  }

  public get slotLineUtil(): SlotLineUtil {
    return this.articleController as SlotLineUtil;
  }

  public selectArticle(id: string, cameraAction = CameraAction.RECENTER) {
    const articleNode = this.articleController.getArticleNode(id);
    if (!articleNode) {
      return console.warn('Given article for selection is not present in the scene');
    }
    this.eventsController.selectArticle(articleNode);
    this.cameraController.disableIdleAnimation();

    switch (cameraAction) {
      case CameraAction.FOCUS:
        return this.cameraController.focusArticle(articleNode);
      case CameraAction.RECENTER:
        return this.cameraController.recenterArticle(articleNode);
    }
  }

  public unselect() {
    this.articleController.clearSlotLineEndCaps();
    this.eventsController.unselect();
    const root = this.articleController.getRootArticleNode();
    this.cameraController.focusWholeConfiguration(root);
    this.cameraController.enableIdleAnimation();
  }

  public async addArticle(
    article: Article,
    slotDefinitions: SlotDefinition[],
    slotToAttachTo: Slot,
    mesh?: AbstractMesh,
  ) {
    const articleMesh = mesh || (await this.assetsController.loadArticleMesh(article));
    const articleNode = new ArticleNode(article, slotDefinitions, articleMesh);
    this.articleController.addArticle(articleNode, slotToAttachTo);
    if (article.definition.logisticsStatus !== LogisticsStatus.ACTIVE) {
      this.eventsController.markDiscontinued(articleNode);
    }

    return articleNode;
  }

  public changeRights(rights: ConfigurationRight[]) {
    this.eventsController.changeRights(rights);
  }

  public async addRootArticle(
    article: Article,
    slotDefinitions: SlotDefinition[],
    positionOfHeightControlPanel?: number,
    mesh?: AbstractMesh,
  ) {
    const rootMesh = mesh || (await this.assetsController.loadArticleMesh(article));
    const articleNode = new ArticleNode(article, slotDefinitions, rootMesh);
    this.articleController.addRootArticle(articleNode, positionOfHeightControlPanel);
    this.lightController.addArticleToLightHierarchy(articleNode);

    return articleNode;
  }

  public removeArticle(id: string) {
    this.articleController.clearSlotLineEndCaps();
    this.articleController.removeArticle(id);
  }

  public async replaceArticle(
    oldArticleId: string,
    newArticle: Article,
    newSlotDefinitions: SlotDefinition[],
    children: ReplacementChild[],
    slot: Slot,
  ) {
    await this.addArticle(newArticle, newSlotDefinitions, slot);
    this.articleController.moveChildren(oldArticleId, newArticle, children);
    this.removeArticle(oldArticleId);
  }

  public async replaceRootArticle(
    oldArticleId: string,
    newArticle: Article,
    newSlotDefinitions: SlotDefinition[],
    children: ReplacementChild[],
    positionOfHeightControlPanel?: number,
  ) {
    await this.addRootArticle(newArticle, newSlotDefinitions, positionOfHeightControlPanel);
    this.articleController.moveChildren(oldArticleId, newArticle, children);
    this.removeArticle(oldArticleId);
  }

  public cleanUpArticles(articleIds: string[]) {
    this.articleController.cleanUpArticles(articleIds);
  }

  public updateArticleColor(id: string, material: string, ralValue: string) {
    const articleNode = this.articleController.getArticleNode(id);
    articleNode.updateColor(material, ralValue);
  }

  public async initFromConfiguration(
    configuration: Configuration,
    showAnimation: boolean,
    selected?: Article,
  ) {
    const loadedArticles = await this.assetsController.loadAllArticles(configuration.articles);

    const positionOfHeightControlPanel = configuration.getPositionOfHeightControlPanel();

    for (const { article, mesh } of loadedArticles) {
      const articleId = article.id;
      const slot = configuration.getAttachedSlot(articleId);
      const slotsOfArticle = configuration.getSlotDefinitionsOfArticle(articleId);
      configuration.root === articleId
        ? await this.addRootArticle(article, slotsOfArticle, positionOfHeightControlPanel, mesh)
        : await this.addArticle(article, slotsOfArticle, slot, mesh);
      // Force render after adding each article
      // Adding articles on init happens faster than babylon render loop which might cause to inconsistent state
      this.scene.render();
    }

    selected && this.selectArticle(selected.id);
    !selected && this.cameraController.enableIdleAnimation();
    const rootNode = this.articleController.getArticleNode(configuration.root);
    if (showAnimation) {
      this.cameraController.startIntroAnimation(rootNode);
    } else {
      this.cameraController.focusWholeConfiguration(rootNode);
    }
  }

  public moveArticleOnSingleSlot(article: Article, slot: Slot) {
    const articleNode = this.articleController.getArticleNode(article.id);
    this.articleController.positionArticleOnSlot(articleNode, slot.parentId, slot.definition);
    this.cameraController.recenterArticle(articleNode);
  }

  public moveOnLineSlot(slotLineChildId: string, position: number, selectedArticle: Article) {
    this.articleController.moveOnLineSlot(slotLineChildId, position);
    if (selectedArticle?.id === slotLineChildId) {
      const articleNode = this.articleController.getArticleNode(selectedArticle.id);
      this.cameraController.recenterArticle(articleNode);
    }
  }

  public calculateDimensions(): Dimensions {
    return this.articleController.calculateDimensions();
  }

  public createScreenshot(): Promise<Base64Image> {
    return this.cameraController.createScreenshot(this.articleController.getRootArticleNode());
  }

  // call this method to generate screenshot for templates
  public createTemplateScreenshot() {
    this.cameraController.createTemplateScreenshot(this.articleController.getRootArticleNode());
  }

  public pauseRendering(): void {
    this.scene.getEngine().stopRenderLoop();
  }

  public resumeRendering(): void {
    this.scene.getEngine().runRenderLoop(() => this.scene.render());
  }

  public setThemePreview(material: string, color: string): void {
    this.articleController.setThemePreview(material, color);
  }

  public resetThemePreview(): void {
    this.articleController.resetThemePreview();
  }

  public applyTheme(material: string, color: string): void {
    this.articleController.applyTheme(material, color);
  }

  public isMounted(): boolean {
    return !!this.scene;
  }

  public resetZoom(): void {
    const root = this.articleController.getRootArticleNode();
    this.cameraController.focusWholeConfiguration(root);
  }

  public zoomIn(): void {
    this.cameraController.zoomIn();
  }

  public zoomOut(): void {
    this.cameraController.zoomOut();
  }

  public async generateGlb(fileName: string): Promise<GLTFData> {
    const renderMeshes = this.articleController
      .getRootArticleNode()
      .mesh.getChildTransformNodes(false);
    renderMeshes.push(this.articleController.getRootArticleNode().mesh);

    const options = {
      shouldExportNode: (node: TransformNode): boolean => renderMeshes.includes(node),
    };
    return await GLTF2Export.GLBAsync(this.scene, fileName, options);
  }

  public reset(): void {
    this.articleController.clearSlotLineEndCaps();
    this.eventsController.unselect();
    this.articleController.reset();
  }

  public dispose(): void {
    const engine = this.scene.getEngine();
    this.scene.dispose();
    engine.dispose();
  }
}
